import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

const inputBase =
  'w-full rounded-control border-[1.5px] border-line-strong bg-paper px-3.5 font-sans text-ink ' +
  'placeholder:text-ink-subtle focus:border-ink focus:outline-none ' +
  'aria-[invalid=true]:border-danger disabled:cursor-not-allowed disabled:bg-surface disabled:text-ink-muted';

type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** `success` shows the hint as a done state (e.g. "Invitation sent"). */
  hintTone?: 'muted' | 'success';
  /** `lg`: 52 px, 17 px text (email on checkout and login). `md`: 48 px (other inputs). */
  size?: 'lg' | 'md';
};

/** Label above, input, then helper text or error below (screens 02, 04, 05, 07). */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hint, error, hintTone = 'muted', size = 'md', id, className = '', ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={inputId} className="text-sm font-semibold">
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={`${inputBase} ${size === 'lg' ? 'h-[52px] text-[17px]' : 'h-12 text-base'} ${className}`}
        {...rest}
      />
      {error ? (
        <span id={`${inputId}-error`} className="text-helper text-danger">
          {error}
        </span>
      ) : hint ? (
        <span
          id={`${inputId}-hint`}
          className={`text-helper ${hintTone === 'success' ? 'font-semibold text-success' : 'text-ink-muted'}`}
        >
          {hint}
        </span>
      ) : null}
    </div>
  );
});

type CheckboxFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  children: ReactNode;
  error?: ReactNode;
  muted?: boolean;
};

/** 22 px checkbox with its sentence (terms, marketing opt-in). */
export const CheckboxField = forwardRef<HTMLInputElement, CheckboxFieldProps>(
  function CheckboxField({ children, error, muted, className = '', ...rest }, ref) {
    return (
      <div className="flex flex-col gap-1">
        <label
          className={`flex items-start gap-3 text-sm leading-snug ${muted ? 'text-ink-muted' : ''} ${className}`}
        >
          <input
            ref={ref}
            type="checkbox"
            aria-invalid={error ? true : undefined}
            className="m-0 size-[22px] shrink-0 accent-ink"
            {...rest}
          />
          <span>{children}</span>
        </label>
        {error && <span className="pl-[34px] text-helper text-danger">{error}</span>}
      </div>
    );
  },
);

type SwitchProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  /** Accessible name when no visible `<label htmlFor>` points at the switch. */
  'aria-label'?: string;
};

/**
 * On/off switch (insurance, screen 02). A real checkbox with role="switch", so forms and
 * react-hook-form work as with any checkbox. Off state from the design; on state uses `accent`.
 */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  { className = '', ...rest },
  ref,
) {
  return (
    <span className={`relative inline-block h-7 w-12 shrink-0 ${className}`}>
      <input
        ref={ref}
        type="checkbox"
        role="switch"
        className="peer absolute inset-0 z-10 m-0 size-full cursor-pointer opacity-0"
        {...rest}
      />
      <span
        aria-hidden="true"
        className="absolute inset-0 rounded-full bg-line-strong transition peer-checked:bg-accent peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent"
      />
      <span
        aria-hidden="true"
        className="absolute left-[3px] top-[3px] size-[22px] rounded-full bg-paper transition peer-checked:translate-x-5"
      />
    </span>
  );
});

type SelectFieldProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  hintTone?: 'muted' | 'success';
};

/** Native select, same look as `TextField` (back-office filters, profile choices). */
export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, hint, error, hintTone = 'muted', id, className = '', children, ...rest },
  ref,
) {
  const autoId = useId();
  const selectId = id ?? autoId;
  const describedBy = error ? `${selectId}-error` : hint ? `${selectId}-hint` : undefined;
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={selectId} className="text-sm font-semibold">
          {label}
        </label>
      )}
      <select
        ref={ref}
        id={selectId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={`${inputBase} h-12 text-base ${className}`}
        {...rest}
      >
        {children}
      </select>
      {error ? (
        <span id={`${selectId}-error`} className="text-helper text-danger">
          {error}
        </span>
      ) : hint ? (
        <span
          id={`${selectId}-hint`}
          className={`text-helper ${hintTone === 'success' ? 'font-semibold text-success' : 'text-ink-muted'}`}
        >
          {hint}
        </span>
      ) : null}
    </div>
  );
});

type TextAreaFieldProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: ReactNode;
  error?: ReactNode;
};

/** Multi-line `TextField` (back-office notes). */
export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  function TextAreaField({ label, error, id, className = '', ...rest }, ref) {
    const autoId = useId();
    const areaId = id ?? autoId;
    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label htmlFor={areaId} className="text-sm font-semibold">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={areaId}
          aria-invalid={error ? true : undefined}
          className={`${inputBase} min-h-24 py-3 text-base ${className}`}
          {...rest}
        />
        {error && <span className="text-helper text-danger">{error}</span>}
      </div>
    );
  },
);
